'use client'
import {
  Button_default
} from "./chunk-EZAUOVA7.mjs";
import {
  NotifyButton_default
} from "./chunk-YMEC2FEU.mjs";

// src/ui/components/league/Group.tsx
import { useMemo } from "react";
import { ImageWithFallback, Paper } from "@wbk/ui";
import { twMerge } from "tailwind-merge";
import { useWindowResize } from "@wbk/hooks";
import { useTranslation } from "@wbk/config";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var COLUMNS = [
  {
    key: "games_played",
    shortLabel: "P",
    getValue: (row) => row.games_played
  },
  {
    key: "wins",
    shortLabel: "W",
    getValue: (row) => row.wins
  },
  {
    key: "draws",
    shortLabel: "D",
    getValue: (row) => row.draws
  },
  {
    key: "losses",
    shortLabel: "L",
    getValue: (row) => row.losses
  },
  {
    key: "for",
    shortLabel: "F",
    getValue: (row) => row.for
  },
  {
    key: "against",
    shortLabel: "A",
    getValue: (row) => row.against
  },
  {
    key: "goal_diff",
    shortLabel: "+/-",
    getValue: (row) => row.goal_diff
  },
  {
    key: "points",
    shortLabel: "Pts",
    getValue: (row) => row.points
  }
];
var LeagueGroup = ({ group, teams, className, hiddenColumns, forceShortLabels }) => {
  const { t } = useTranslation(["ticketing_league"]);
  const { width } = useWindowResize();
  const isMobile = width < 768;
  const visibleColumns = useMemo(() => {
    const base = COLUMNS.filter((col) => !(hiddenColumns == null ? void 0 : hiddenColumns.includes(col.key)));
    const mobileColumnKeys = ["games_played", "goal_diff", "points"];
    if (isMobile) {
      return base.filter((col) => mobileColumnKeys.includes(col.key));
    }
    return base;
  }, [hiddenColumns, isMobile]);
  const teamsMap = useMemo(() => {
    const map = {};
    teams == null ? void 0 : teams.forEach((team) => {
      map[team._id] = team;
    });
    return map;
  }, [teams]);
  const sortedTable = useMemo(() => {
    var _a;
    if (!((_a = group.points_table) == null ? void 0 : _a.table)) return [];
    const num = (v) => typeof v === "number" ? v : null;
    return [...group.points_table.table].sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      if (b.goal_diff !== a.goal_diff) {
        return b.goal_diff - a.goal_diff;
      }
      const aFor = num(a.for);
      const bFor = num(b.for);
      if (aFor !== null && bFor !== null && bFor !== aFor) {
        return bFor - aFor;
      }
      const aAgainst = num(a.against);
      const bAgainst = num(b.against);
      if (aAgainst !== null && bAgainst !== null && aAgainst !== bAgainst) {
        return aAgainst - bAgainst;
      }
      if (b.wins !== a.wins) {
        return b.wins - a.wins;
      }
      if (b.games_played !== a.games_played) {
        return a.games_played - b.games_played;
      }
      return 0;
    });
  }, [group.points_table]);
  if (!group.points_table) return null;
  return /* @__PURE__ */ jsx(Paper, { className: twMerge("overflow-hidden !p-0", className), children: /* @__PURE__ */ jsx("div", { className: "overflow-auto", children: /* @__PURE__ */ jsxs("table", { className: "min-w-full text-sm", children: [
    /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "text-body-contrast border-paper border-b", children: [
      /* @__PURE__ */ jsx("th", { className: "px-L py-S text-start text-lg", children: group.title }),
      visibleColumns.map((col) => /* @__PURE__ */ jsx("th", { className: "p-3 text-center", children: forceShortLabels ? /* @__PURE__ */ jsx("span", { children: col.shortLabel }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("span", { className: "hidden md:inline", children: t(`ticketing_league:table.${col.key}`) }),
        /* @__PURE__ */ jsx("span", { className: "md:hidden", children: col.shortLabel })
      ] }) }, col.key))
    ] }) }),
    /* @__PURE__ */ jsx("tbody", { children: sortedTable.map((row, index) => {
      const team = teamsMap[row.team_id];
      return /* @__PURE__ */ jsxs("tr", { className: "border-paper border-b last:border-b-0", children: [
        /* @__PURE__ */ jsxs("td", { className: "px-L py-S flex items-center gap-1", children: [
          /* @__PURE__ */ jsxs("span", { className: "min-w-4", children: [
            index + 1,
            " "
          ] }),
          /* @__PURE__ */ jsx(
            ImageWithFallback,
            {
              src: team == null ? void 0 : team.logo,
              alt: (team == null ? void 0 : team.name) || "Unknown",
              className: "h-8 w-8 shrink-0 rounded-full object-contain"
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "ps-S shrink-0", children: (team == null ? void 0 : team.name) || "Unknown" })
        ] }),
        visibleColumns.map((col) => /* @__PURE__ */ jsx(
          "td",
          {
            className: "text-text-secondary last:text-text px-M text-center font-semibold md:w-20 lg:w-32",
            children: col.getValue(row)
          },
          col.key
        ))
      ] }, row.team_id);
    }) })
  ] }) }) });
};
var Group_default = LeagueGroup;

// src/ui/components/league/Item.tsx
import { formatDate } from "@wbk/utils";
import { twMerge as twMerge2 } from "tailwind-merge";
import { Button, ImageWithPlaceholder, Paper as Paper2, Typography } from "@wbk/ui";
import { ClockIcon, LocationIcon } from "@wbk/svg";
import { CurrencyPriceLabel } from "@wbk/currency";
import { useWindowResize as useWindowResize2 } from "@wbk/hooks";
import { Link, useRouter, useTranslation as useTranslation2 } from "@wbk/config";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var LeagueItem = ({ event, mode, className }) => {
  var _a, _b, _c, _d, _e;
  const { t } = useTranslation2(["ticketing_league"]);
  const { locale } = useRouter();
  const { width } = useWindowResize2();
  const isMobile = width < 968;
  const effectiveMode = mode === "list" && isMobile ? "grid" : mode;
  const isMatch = !!((_a = event == null ? void 0 : event.home_team) == null ? void 0 : _a._id) && !!((_b = event == null ? void 0 : event.away_team) == null ? void 0 : _b._id);
  const posterImage = event.mobile_poster || event.poster;
  const home = event == null ? void 0 : event.home_team;
  const away = event == null ? void 0 : event.away_team;
  const notifyMe = event == null ? void 0 : event.is_notify_me_enabled;
  const isScore = !!(home == null ? void 0 : home.score) && !!(away == null ? void 0 : away.score);
  const dateLabel = effectiveMode === "list" ? formatDate(event.start_date_time, `${locale}-GB`, { day: "2-digit" }) : formatDate(event.start_date_time, `${locale}-GB`, {
    dateStyle: "long",
    timeStyle: "short"
  });
  const month = effectiveMode === "list" ? formatDate(event.start_date_time, `${locale}-GB`, { month: "short" }) : "";
  const timeLabel = formatDate(event.start_date_time, `${locale}-GB`, {
    timeStyle: "short"
  });
  const location = [(_c = event.venue) == null ? void 0 : _c.name, (_d = event.venue) == null ? void 0 : _d.city, (_e = event.venue) == null ? void 0 : _e.country].filter(Boolean).join(", ");
  return /* @__PURE__ */ jsxs2(
    Paper2,
    {
      className: twMerge2(
        "flex flex-col items-center overflow-hidden",
        effectiveMode === "list" ? "flex-row justify-center gap-4 md:justify-start" : "space-y-4",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs2("div", { className: "px-5 text-center", children: [
          /* @__PURE__ */ jsx2(Typography, { variant: effectiveMode === "list" ? "heading-L" : "label-S", children: dateLabel }),
          effectiveMode === "list" && /* @__PURE__ */ jsx2(Typography, { variant: "body-M", color: "text-secondary", children: month })
        ] }),
        effectiveMode === "list" && /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 h-16 w-px" }),
        isMatch ? /* @__PURE__ */ jsxs2(
          "div",
          {
            className: twMerge2(
              effectiveMode === "list" ? "flex items-center gap-2 px-2" : "flex h-full w-full items-center gap-2 text-center"
            ),
            children: [
              /* @__PURE__ */ jsxs2(
                "div",
                {
                  className: twMerge2(
                    effectiveMode === "list" ? "" : "flex w-full flex-col items-center justify-center"
                  ),
                  children: [
                    /* @__PURE__ */ jsx2(
                      ImageWithPlaceholder,
                      {
                        src: home == null ? void 0 : home.logo,
                        alt: home == null ? void 0 : home.name,
                        className: twMerge2(
                          "aspect-1 object-contain",
                          effectiveMode === "list" ? "h-16 w-16" : "mb-2 h-28 w-28"
                        )
                      }
                    ),
                    effectiveMode === "grid" && /* @__PURE__ */ jsx2(Typography, { variant: "label-L", children: home == null ? void 0 : home.name })
                  ]
                }
              ),
              /* @__PURE__ */ jsx2(
                "div",
                {
                  className: twMerge2(
                    effectiveMode === "list" ? "rounded-XS shrink-0 p-1 text-center" : "rounded-XS w-16 shrink-0 bg-white/5 p-2 text-center"
                  ),
                  children: isScore ? /* @__PURE__ */ jsxs2(Typography, { strong: true, color: "text-secondary", children: [
                    home == null ? void 0 : home.score,
                    " - ",
                    away == null ? void 0 : away.score
                  ] }) : /* @__PURE__ */ jsx2(Typography, { strong: true, color: "text-secondary", children: "VS" })
                }
              ),
              /* @__PURE__ */ jsxs2(
                "div",
                {
                  className: twMerge2(
                    effectiveMode === "list" ? "" : "flex w-full flex-col items-center justify-center"
                  ),
                  children: [
                    /* @__PURE__ */ jsx2(
                      ImageWithPlaceholder,
                      {
                        src: away == null ? void 0 : away.logo,
                        alt: away == null ? void 0 : away.name,
                        className: twMerge2(
                          "aspect-1 object-contain",
                          effectiveMode === "list" ? "h-16 w-16" : "mb-2 h-28 w-28"
                        )
                      }
                    ),
                    effectiveMode === "grid" && /* @__PURE__ */ jsx2(Typography, { variant: "label-L", children: away == null ? void 0 : away.name })
                  ]
                }
              )
            ]
          }
        ) : /* @__PURE__ */ jsxs2("div", { className: "px-2", children: [
          /* @__PURE__ */ jsx2(
            ImageWithPlaceholder,
            {
              src: posterImage,
              alt: event.title,
              className: twMerge2(
                "aspect-1 rounded-md object-cover",
                effectiveMode === "list" ? "w-16" : "mx-auto w-full max-w-64"
              )
            }
          ),
          effectiveMode === "grid" && /* @__PURE__ */ jsx2(Typography, { variant: "label-L", className: "pt-2", children: event.title })
        ] }),
        effectiveMode === "grid" && location && /* @__PURE__ */ jsx2(Typography, { variant: "label-S", color: "text-secondary", children: location }),
        effectiveMode === "list" && /* @__PURE__ */ jsxs2("div", { className: "flex grow flex-col items-start justify-start space-y-2", children: [
          isMatch ? /* @__PURE__ */ jsxs2("div", { className: "flex items-end gap-2", children: [
            /* @__PURE__ */ jsx2(Typography, { variant: "label-L", children: home.name }),
            /* @__PURE__ */ jsx2(Typography, { variant: "label-S", color: "text-secondary", children: "VS" }),
            /* @__PURE__ */ jsx2(Typography, { variant: "label-L", children: away.name })
          ] }) : /* @__PURE__ */ jsx2(Typography, { variant: "label-L", children: event.title }),
          /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-4", children: [
            timeLabel && /* @__PURE__ */ jsxs2("div", { className: "flex shrink-0 items-center gap-1", children: [
              /* @__PURE__ */ jsx2(ClockIcon, { className: "text-text-secondary w-4" }),
              /* @__PURE__ */ jsx2(Typography, { variant: "body-S", color: "text-secondary", children: timeLabel })
            ] }),
            location && /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx2(LocationIcon, { className: "text-text-secondary w-4" }),
              /* @__PURE__ */ jsx2(Typography, { variant: "body-S", color: "text-secondary", className: "line-clamp-1", children: location })
            ] })
          ] })
        ] }),
        !isScore && /* @__PURE__ */ jsx2(
          "div",
          {
            className: twMerge2(effectiveMode === "list" ? "" : "flex w-full flex-1 flex-col gap-3"),
            children: notifyMe ? /* @__PURE__ */ jsx2(
              NotifyButton_default,
              {
                className: "mx-auto max-w-md font-semibold",
                shape: "solid",
                theme: "action",
                event: {
                  __typename: "event",
                  id: event._id,
                  title: event.title,
                  slug: event.slug
                }
              }
            ) : /* @__PURE__ */ jsxs2(
              "div",
              {
                className: twMerge2(
                  "flex gap-2",
                  effectiveMode === "list" ? "items-center" : "flex-1 flex-col justify-end"
                ),
                children: [
                  /* @__PURE__ */ jsxs2(
                    "div",
                    {
                      className: twMerge2(
                        effectiveMode === "list" ? "flex items-end gap-1.5" : "mx-auto flex w-fit items-center gap-2 text-center"
                      ),
                      children: [
                        /* @__PURE__ */ jsx2(Typography, { variant: "label-S", color: "text-secondary", children: t("ticketing_league:from") }),
                        /* @__PURE__ */ jsx2(
                          CurrencyPriceLabel,
                          {
                            originalPrice: event.event_start_price,
                            currency: event.currency,
                            className: "text-text font-semibold"
                          }
                        )
                      ]
                    }
                  ),
                  event.resale_status && /* @__PURE__ */ jsx2(
                    Button_default,
                    {
                      slug: event.slug,
                      shape: "outlined",
                      theme: "action",
                      className: "mx-auto w-full max-w-md",
                      title: /* @__PURE__ */ jsx2(Typography, { className: "line-clamp-1 font-semibold", children: t("ticketing_common:resell.check_tickets") })
                    }
                  ),
                  /* @__PURE__ */ jsx2(
                    Button,
                    {
                      as: Link,
                      to: `/${locale}/events/${event.slug}`,
                      theme: "primary",
                      className: twMerge2(
                        "mx-auto border border-transparent",
                        effectiveMode === "list" ? "shrink-0" : "w-full max-w-md"
                      ),
                      children: /* @__PURE__ */ jsx2(Typography, { className: "font-semibold", children: t("ticketing_league:book_now") })
                    }
                  )
                ]
              }
            )
          }
        )
      ]
    }
  );
};
var Item_default = LeagueItem;

export {
  Group_default,
  Item_default
};
//# sourceMappingURL=chunk-GQQRQPJI.mjs.map